/**
 * The plugins mirrored from the core (`plugins.list`): the built-in packs and language plugins
 * and the community ones installed, with what they add (requests, commands, windows), the
 * permissions they ask for, and what can be done with them (install, approve and turn on, turn
 * off, remove). Which ones are on is the project's (or the space's) choice, kept in its graph:
 * a `graph.changed` choosing other plugins (someone else's change, an undo, another project
 * opened) brings the list again. Kept here so the list survives the Plugins window being closed, and shared with
 * the Analyze window (which shows which plugins a scan uses).
 *
 * Plugin commands are named `plugin:<id>:<command>` ({@link pluginCommand}): a shortcut or a
 * menu entry runs them through the command registry ({@link pluginCommandResolver}). What
 * plugins say (`plugins.notice`) is shown as toasts.
 *
 * Installing works two ways: a folder path the core reads itself (a core with a disk: server,
 * desktop, CLI), or the plugin's files read by the page and sent in base64 (the browser build,
 * or a plugin dropped on the window).
 */
import { createSignal, type Accessor } from "solid-js";
import type { PluginFile } from "@protocol/PluginFile";
import type { PluginInfo } from "@protocol/PluginInfo";
import type { Request } from "@protocol/Request";
import { isClientError, type Client } from "../client/client";
import { changesPlugins } from "../client/graph";
import { toBase64 } from "../shared/base64";
import type { Command } from "../shared/commands";
import { toast } from "../shared/toast";
import type { MenuSection } from "../shared/menus";

/** The prefix of plugin windows and commands: `plugin:<id>:<name>`. */
export const PLUGIN_PREFIX = "plugin:";

/** The name of a plugin's command or window: `plugin:<id>:<name>`. */
export function pluginCommand(id: string, name: string): string {
  return `${PLUGIN_PREFIX}${id}:${name}`;
}

/** The plugin id and the part's name in `plugin:<id>:<name>`, or `null` for any other name. */
export function parsePluginPart(name: string): { plugin: string; part: string } | null {
  const match = /^plugin:([^:]+):([^:]+)$/.exec(name);
  return match ? { plugin: match[1]!, part: match[2]! } : null;
}

/** Whether a plugin can be used now: turned on, approved, working, with what it requires on. */
export function isReady(plugin: PluginInfo): boolean {
  return plugin.enabled && !plugin.needs_approval && !plugin.problem && !plugin.missing.length;
}

/** A shelf of the plugin browser: every plugin, the built-in or installed ones, or one category. */
export interface Shelf {
  /** `all`, `builtin`, `installed`, or `category:<name>`. */
  id: string;
  /** What the browser's side says. */
  label: string;
  /** How many plugins are on it. */
  count: number;
}

/** The shelves of `list`: All, Built in, Installed, then each category in the order met (packs first, as the core lists them). */
export function shelvesOf(list: readonly PluginInfo[]): Shelf[] {
  const categories: string[] = [];
  for (const p of list) if (p.category && !categories.includes(p.category)) categories.push(p.category);
  return [
    { id: "all", label: "All plugins", count: list.length },
    { id: "builtin", label: "Built in", count: list.filter((p) => p.builtin).length },
    { id: "installed", label: "Installed", count: list.filter((p) => !p.builtin).length },
    ...categories.map((c) => ({ id: `category:${c}`, label: c, count: list.filter((p) => p.category === c).length })),
  ];
}

/** Whether `plugin` is on `shelf` and matches `query` (in its name, id, description, author, category or language). */
export function pluginShown(plugin: PluginInfo, shelf: string, query: string): boolean {
  const onShelf = shelf === "all" || (shelf === "builtin" ? plugin.builtin : shelf === "installed" ? !plugin.builtin : shelf === `category:${plugin.category}`);
  const q = query.trim().toLowerCase();
  const text = [plugin.name, plugin.id, plugin.description, plugin.author, plugin.category, plugin.language].join(" ").toLowerCase();
  return onShelf && (!q || text.includes(q));
}

/** The manifest's file name: every plugin folder has one. */
export const MANIFEST = "plugin.kdl";

/** What the service needs. */
export interface PluginsDeps {
  client: Client;
  /** Whether the core reads folders on its disk (not the browser build). */
  readsFolders: boolean;
  /** Asks for a folder with the system's dialog (desktop app only). */
  pickFolder?: () => Promise<string | null>;
}

/** The plugins service. */
export interface Plugins {
  /** Whether a plugin can be installed from a folder path the core reads. */
  readsFolders: boolean;
  /** Every plugin: built in first, then installed ones. */
  list: Accessor<PluginInfo[]>;
  /** The folder installed plugins live in (`null` in the browser build). */
  folder: Accessor<string | null>;
  /** `true` while a request runs. */
  busy: Accessor<boolean>;
  /** Why the last action failed, if it did. */
  error: Accessor<string | null>;
  /** Asks the core for the list again (it also re-reads the plugin folder). */
  refresh(): Promise<void>;
  /** Turns a plugin on or off. Turning one on approves the permissions it asks for. */
  setEnabled(id: string, enabled: boolean): Promise<boolean>;
  /** Runs a plugin's command; its notices come as toasts. `false` if it failed (toast too). */
  run(id: string, command: string): Promise<boolean>;
  /** The commands of the plugins ready to use: name (`plugin:<id>:<command>`) and label. */
  commands(): { name: string; label: string }[];
  /** Removes an installed plugin. */
  remove(id: string): Promise<boolean>;
  /** Installs the plugin in a folder the core reads. */
  installFolder(path: string): Promise<boolean>;
  /** Installs a plugin from files read in the page (its `plugin.kdl` and `.wasm` file). */
  installFiles(files: ArrayLike<File>): Promise<boolean>;
  /** Asks for a plugin folder with the system's dialog and installs it; `undefined` where there is none. */
  browse?: () => Promise<boolean>;
  /** The plugins a scan uses: ready ones that read code. */
  active(): PluginInfo[];
  /** Finds the command a `plugin:<id>:<command>` name stands for (for the command registry). */
  resolve(name: string): Command | undefined;
}

/** The last part of a path (`a/b/plugin.kdl` → `plugin.kdl`). */
function baseName(file: File): string {
  const path = file.webkitRelativePath || file.name;
  return path.slice(path.lastIndexOf("/") + 1);
}

/**
 * The files of a plugin worth sending: its manifest and `.wasm` files at its top level (a
 * picked folder's sub-folders, such as sources, are left out).
 */
export function pluginFiles(files: ArrayLike<File>): File[] {
  return Array.from(files).filter((file) => {
    const depth = (file.webkitRelativePath || file.name).split("/").length;
    const name = baseName(file);
    return depth <= 2 && (name === MANIFEST || name.endsWith(".wasm"));
  });
}

/** Starts mirroring the plugins through `deps.client`. */
export function createPlugins(deps: PluginsDeps): Plugins {
  const { client } = deps;
  const [list, setList] = createSignal<PluginInfo[]>([]);
  const [folder, setFolder] = createSignal<string | null>(null);
  const [busy, setBusy] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  /** Sends a request answered by the plugin list; keeps the list or the reason it failed. */
  const send = async (request: Request, done?: string): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const response = await client.request(request);
      if (response.type !== "plugins") return false;
      setList(response.plugins);
      setFolder(response.folder);
      if (done) toast(done);
      return true;
    } catch (e) {
      setError(isClientError(e) ? e.message : String(e));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const refresh = async () => {
    await send({ method: "plugins.list" });
  };
  client.onOpen(() => void refresh());
  client.onEvent((event) => {
    if (event.event === "plugins.changed") setList(event.data.plugins);
    if (event.event === "graph.changed" && event.data.changes.some(changesPlugins)) void refresh();
    if (event.event === "plugins.notice") {
      const name = list().find((p) => p.id === event.data.plugin)?.name ?? event.data.plugin;
      toast(`${name}: ${event.data.text}`, event.data.level === "error" ? "error" : "info");
    }
  });

  /** Runs a command of a ready plugin. */
  const run = async (id: string, command: string) => {
    try {
      await client.request({ method: "plugins.command", params: { plugin: id, command } });
      return true;
    } catch (e) {
      toast(isClientError(e) ? e.message : String(e), "error");
      return false;
    }
  };
  /** The commands of the ready plugins. */
  const commands = () => list().filter(isReady).flatMap((p) => p.commands.map((c) => ({ name: pluginCommand(p.id, c.name), label: c.label })));

  /** Installs `path`, the toast naming what was installed. */
  const installFolder = async (path: string) => {
    const folder = path.trim();
    if (!folder) return false;
    return send({ method: "plugins.install", params: { source: { folder: { path: folder } } } }, "Plugin installed.");
  };

  return {
    readsFolders: deps.readsFolders,
    list,
    folder,
    busy,
    error,
    refresh,
    setEnabled: (id, enabled) => send({ method: "plugins.enable", params: { id, enabled } }),
    remove: (id) => send({ method: "plugins.remove", params: { id } }, `Plugin ${id} removed.`),
    run,
    commands,
    installFolder,
    async installFiles(picked) {
      const files = pluginFiles(picked);
      if (!files.some((f) => baseName(f) === MANIFEST)) {
        setError(`no ${MANIFEST} among the chosen files: choose the plugin's folder, or its ${MANIFEST} and .wasm file`);
        return false;
      }
      const encoded: PluginFile[] = await Promise.all(
        files.map(async (file) => ({ name: baseName(file), base64: toBase64(new Uint8Array(await file.arrayBuffer())) })),
      );
      return send({ method: "plugins.install", params: { source: { files: { files: encoded } } } }, "Plugin installed.");
    },
    browse: deps.pickFolder
      ? async () => {
          const path = await deps.pickFolder!();
          return path ? installFolder(path) : false;
        }
      : undefined,
    active: () => list().filter((p) => isReady(p) && !!p.language),
    resolve(name) {
      const part = parsePluginPart(name);
      if (!part || !commands().some((c) => c.name === name)) return undefined;
      return () => void run(part.plugin, part.part);
    },
  };
}

/** The Tools menu's plugins: the Plugins window, then every plugin command. */
export function pluginMenus(plugins: Plugins): MenuSection[] {
  return [
    {
      menu: "tools",
      section: "Plugins",
      order: 30,
      entries: () => [{ label: "Plugins…", command: "window.open", argument: "plugins" }, ...plugins.commands().map((c) => ({ label: c.label, command: c.name }))],
    },
  ];
}

